import { useEffect, useState } from "react";
import { Clipboard, Link2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { errMsg } from "../lib/errors";

/**
 * UploadExtras — drop-in helper UI for any upload zone.
 *
 * Adds three input methods on top of the existing file-picker / drag-and-drop:
 *   1. Paste from clipboard (Ctrl/⌘+V or button) — yields the image File(s)
 *      to onFiles. Listener is wired on the document while `pasteEnabled`.
 *   2. URL import — opens a small dialog, POSTs to /patients/{pid}/files-from-url
 *      and bubbles the resulting FileOut to onFileImported.
 *
 * Drag-and-drop is left to the caller's existing dropzone; this component only
 * exposes a discoverable "Drop / Paste / Import URL" hint row so clinicians
 * see all three methods at a glance.
 *
 * Props:
 *   patientId           string (required for URL import) — null disables it
 *   onFiles(files)      callback when paste yields File objects (caller should
 *                       run the same upload path it uses for the file picker)
 *   onFileImported(rec) callback when URL import returns a FileOut doc
 *   pasteEnabled        default true; disable when an unrelated dialog is open
 *   compact             default false; renders a thinner row
 */
export default function UploadExtras({
  patientId,
  onFiles,
  onFileImported,
  pasteEnabled = true,
  compact = false,
}) {
  const [urlOpen, setUrlOpen] = useState(false);

  // Global paste listener — picks up images from the OS clipboard.
  useEffect(() => {
    if (!pasteEnabled) return;
    const handler = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files = [];
      for (const it of items) {
        if (it.kind === "file") {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length === 0) return;
      // Only act on image clipboard payloads — text paste should still work.
      if (!files.some((f) => /^image\//i.test(f.type))) return;
      e.preventDefault();
      onFiles?.(files);
      toast.success(`Pasted ${files.length} image${files.length === 1 ? "" : "s"}`);
    };
    document.addEventListener("paste", handler);
    return () => document.removeEventListener("paste", handler);
  }, [pasteEnabled, onFiles]);

  return (
    <>
      <div
        data-testid="upload-extras-row"
        className={`mt-2 flex flex-wrap items-center gap-2 ${
          compact ? "text-[10px]" : "text-[11px]"
        } text-[var(--pa-text-dim)]`}
      >
        <span className="text-[var(--pa-text-muted)]">Also:</span>
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[var(--pa-bg-2)] border border-[var(--pa-border)] text-[var(--pa-text-muted)]">
          <Clipboard size={10} /> Paste image (⌘/Ctrl + V)
        </span>
        {patientId && (
          <button
            type="button"
            onClick={() => setUrlOpen(true)}
            data-testid="upload-extras-url-btn"
            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[var(--pa-bg-2)] border border-[var(--pa-border)] text-[var(--pa-gold)] hover:border-[var(--pa-gold)]/60 transition"
          >
            <Link2 size={10} /> Import from URL
          </button>
        )}
      </div>

      {urlOpen && (
        <UrlImportDialog
          patientId={patientId}
          onClose={() => setUrlOpen(false)}
          onImported={(rec) => {
            setUrlOpen(false);
            onFileImported?.(rec);
            toast.success("File imported from URL");
          }}
        />
      )}
    </>
  );
}

function UrlImportDialog({ patientId, onClose, onImported }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const u = url.trim();
    if (!/^https?:\/\//i.test(u)) {
      toast.error("URL must start with http(s)://");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/patients/${patientId}/files-from-url`, { url: u });
      onImported?.(r.data);
    } catch (err) {
      toast.error(errMsg(err, "URL import failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="upload-url-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
    >
      <form
        onSubmit={submit}
        className="relative w-full max-w-md bg-[var(--pa-surface)] border border-[var(--pa-gold)]/40 rounded-lg p-5 shadow-[0_0_60px_-10px_rgba(201,168,76,0.35)]"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1 rounded text-[var(--pa-text-dim)] hover:text-[var(--pa-text)]"
          title="Close"
        >
          <X size={14} />
        </button>
        <div className="kicker">Import from URL</div>
        <h3 className="font-serif text-xl text-[var(--pa-text)] mt-1">
          Pull a file from cloud storage
        </h3>
        <p className="text-[11px] text-[var(--pa-text-muted)] mt-2 leading-relaxed">
          Paste a direct download URL — Dropbox, Google Drive (use the "Open in new tab"
          direct-share link), S3 presigned URL, or any public CDN. Max 500 MB. DICOM
          (.dcm) files are auto-extracted into PNG slices.
        </p>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          autoFocus
          data-testid="upload-url-input"
          placeholder="https://…"
          className="mt-4 w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-2.5"
        />
        <button
          type="submit"
          disabled={busy || !url.trim()}
          data-testid="upload-url-submit"
          className="mt-5 w-full bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded-md px-4 py-2.5 hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {busy ? (
            <>
              <Loader2 size={13} className="animate-spin" /> Importing…
            </>
          ) : (
            "Import file"
          )}
        </button>
      </form>
    </div>
  );
}
